import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import "../styles/Regi.css";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const Regi = () => {
  const [formData, setFormData] = useState({
    cs_user_name: "",
    cs_name: "",
    cs_user_email: "",
    cs_user_phone: "",
    cs_user_department: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [departmentsError, setDepartmentsError] = useState("");
  const [otpStep, setOtpStep] = useState("form");
  const [otp, setOtp] = useState("");
  const [otpKey, setOtpKey] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [createdUser, setCreatedUser] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setDepartmentsLoading(true);
        setDepartmentsError("");
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found.");
        }
        const response = await axios.get(
          `${API_BASE_URL}/api/departments-admin`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.status === 200) {
          setDepartments(response.data.departments || []);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        setDepartmentsError(
          "Failed to load departments. Please try again later."
        );
        setDepartments([]);
      } finally {
        setDepartmentsLoading(false);
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    let timer;
    if (otpCountdown > 0) {
      timer = setInterval(() => {
        setOtpCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [otpCountdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cs_user_department") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        cs_user_name: value,
      }));
      if (errors.cs_user_name) {
        setErrors((prev) => ({ ...prev, cs_user_name: "" }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (errorMessage) {
      setErrorMessage("");
    }
    if (otpError) {
      setOtpError("");
    }
  };

  const handleDepartmentChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({
      ...prev,
      cs_user_department: value,
      cs_user_name: value,
    }));
    if (errors.cs_user_name || errors.cs_user_department) {
      setErrors((prev) => ({
        ...prev,
        cs_user_name: "",
        cs_user_department: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    if (!formData.cs_user_name.trim())
      newErrors.cs_user_name = "Department selection is required";
    if (!formData.cs_name.trim()) newErrors.cs_name = "Full name is required";
    if (!formData.cs_user_email.trim())
      newErrors.cs_user_email = "Email is required";
    if (!formData.cs_user_department)
      newErrors.cs_user_department = "Department is required";

    if (formData.cs_user_email && !emailRegex.test(formData.cs_user_email)) {
      newErrors.cs_user_email = "Invalid email format";
    }

    if (!formData.cs_user_phone || !formData.cs_user_phone.trim()) {
      newErrors.cs_user_phone = "Phone number is required for verification";
    } else {
      const cleanPhone = formData.cs_user_phone.replace(/\D/g, "");
      if (!phoneRegex.test(cleanPhone)) {
        newErrors.cs_user_phone = "Valid 10-digit phone number required";
      }
    }

    return newErrors;
  };

  const sendVerificationOTP = async () => {
    if (!formData.cs_user_phone) {
      const errorMsg = "Phone number is required";
      setOtpError(errorMsg);
      setErrorMessage(errorMsg);
      return false;
    }

    setOtpLoading(true);
    setOtpError("");
    setErrorMessage("");

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        const errorMsg = "Session expired. Please login again.";
        setOtpError(errorMsg);
        setErrorMessage(errorMsg);
        setTimeout(() => {
          localStorage.clear();
          window.location.href = "/login";
        }, 2000);
        return false;
      }

      const cleanPhone = formData.cs_user_phone.replace(/\D/g, "");

      const response = await axios.post(
        `${API_BASE_URL}/api/user-creation/send-otp-admin`,
        {
          phone: cleanPhone,
          name: formData.cs_name,
          email: formData.cs_user_email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setOtpKey(response.data.otpKey);
        setOtpStep("verify-otp");
        setOtpCountdown(response.data.expiresIn || 300);
        setSuccessMessage(
          `OTP sent to ${cleanPhone} via SMS. Please check your phone.`
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error sending OTP:", error);

      if (error.response) {
        const errorData = error.response.data;
        let errorMsg = errorData.message || "Failed to send OTP";

        if (errorData.details && errorData.details.message) {
          errorMsg = `${errorMsg}: ${errorData.details.message}`;
        }

        switch (error.response.status) {
          case 409:
            const conflictError = "This phone number is already registered to another user. Please use a different phone number.";
            setOtpError(conflictError);
            setErrorMessage(conflictError);
            break;
          case 400:
            const badRequestError = errorMsg || "Invalid phone number format";
            setOtpError(badRequestError);
            setErrorMessage(badRequestError);
            break;
          case 401:
            const unauthorizedError = "Session expired. Please login again.";
            setOtpError(unauthorizedError);
            setErrorMessage(unauthorizedError);
            setTimeout(() => {
              localStorage.clear();
              window.location.href = "/login";
            }, 2000);
            break;
          case 403:
            const forbiddenError = "Permission denied. Please login again.";
            setOtpError(forbiddenError);
            setErrorMessage(forbiddenError);
            break;
          case 500:
            const serverError = errorMsg || "SMS service failed. Please try again later.";
            setOtpError(serverError);
            setErrorMessage(serverError);
            break;
          default:
            const defaultError = errorMsg || "Failed to send OTP. Please try again.";
            setOtpError(defaultError);
            setErrorMessage(defaultError);
        }
      } else if (error.request) {
        const networkError = "Network error. Please check your internet connection.";
        setOtpError(networkError);
        setErrorMessage(networkError);
      } else {
        const unknownError = "An error occurred. Please try again.";
        setOtpError(unknownError);
        setErrorMessage(unknownError);
      }
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOTPAndCreateUser = async () => {
    if (!otp.trim() || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setOtpLoading(true);
    setOtpError("");
    setErrorMessage("");

    try {
      const token = localStorage.getItem("token");
      const cleanPhone = formData.cs_user_phone.replace(/\D/g, "");

      const userData = {
        otpKey,
        otp,
        cs_user_name: formData.cs_user_name,
        cs_name: formData.cs_name,
        cs_user_email: formData.cs_user_email,
        cs_user_phone: cleanPhone,
        cs_user_department: formData.cs_user_department,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/user-creation/create-admin`,
        userData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setCreatedUser(response.data.user);
        setShowSuccessDialog(true);
        setOtpStep("form");
        setOtp("");
        setOtpKey("");
        setOtpCountdown(0);
        setOtpError("");
        setSuccessMessage("");
        setErrorMessage("");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);

      if (error.response) {
        const errorData = error.response.data;
        let errorMsg = errorData.message || "Failed to create user";

        if (errorData.details && errorData.details.server_error) {
          errorMsg = `${errorMsg}: ${errorData.details.server_error}`;
        }

        switch (error.response.status) {
          case 400:
            setOtpError(
              errorMsg || "Invalid OTP or expired. Please try again."
            );
            break;
          case 409:
            setOtpError(
              "Username or email already exists. Please use different credentials."
            );
            break;
          case 401:
            setOtpError("Session expired. Please login again.");
            setTimeout(() => {
              localStorage.clear();
              window.location.href = "/login";
            }, 2000);
            break;
          case 403:
            setOtpError("Permission denied. Please login again.");
            break;
          default:
            setOtpError(errorMsg || "Failed to create user. Please try again.");
        }
      } else if (error.request) {
        setOtpError(
          "Network error. Please check your connection and try again."
        );
      } else {
        setOtpError("An error occurred. Please try again.");
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSuccessMessage("");
    setErrorMessage("");
    setOtpError("");

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    await sendVerificationOTP();
    setIsSubmitting(false);
  };

  const resendOTP = async () => {
    if (otpCountdown > 60) {
      setOtpError(
        `Please wait ${Math.floor(otpCountdown / 60)} minutes before resending`
      );
      return;
    }

    setOtpLoading(true);
    setOtpError("");

    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${API_BASE_URL}/api/resend-otp-admin`,
        { otpKey },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setOtpCountdown(300);
        setSuccessMessage("New OTP sent successfully!");
      } else {
        const errorData = response.data;
        let errorMsg = errorData.message || "Failed to resend OTP";

        if (errorData.details && errorData.details.message) {
          errorMsg = `${errorMsg}: ${errorData.details.message}`;
        }

        setOtpError(errorMsg);
      }
    } catch (error) {
      console.error("Resend OTP error:", error);

      if (error.response) {
        const errorData = error.response.data;
        let errorMsg = errorData.message || "Failed to resend OTP";

        if (errorData.details && errorData.details.message) {
          errorMsg = `${errorMsg}: ${errorData.details.message}`;
        }

        setOtpError(errorMsg);
      } else if (error.request) {
        setOtpError("Network error. Please check your connection.");
      } else {
        setOtpError("An error occurred. Please try again.");
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
    setFormData({
      cs_user_name: "",
      cs_name: "",
      cs_user_email: "",
      cs_user_phone: "",
      cs_user_department: "",
    });
    setErrors({});
    setSuccessMessage("");
    setErrorMessage("");
    setOtp("");
    setOtpStep("form");
    setOtpError("");
    setCreatedUser(null);
    setOtpCountdown(0);
    window.location.reload();
  };

  const handleReset = () => {
    setFormData({
      cs_user_name: "",
      cs_name: "",
      cs_user_email: "",
      cs_user_phone: "",
      cs_user_department: "",
    });
    setErrors({});
    setSuccessMessage("");
    setErrorMessage("");
    setOtp("");
    setOtpStep("form");
    setOtpError("");
    setCreatedUser(null);
    setOtpCountdown(0);
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const cancelOTPVerification = () => {
    setOtpStep("form");
    setOtp("");
    setOtpError("");
    setOtpCountdown(0);
    setSuccessMessage("");
    setErrorMessage("");
  };

  const departmentOptions = departments.map((dept) => ({
    value: dept,
    label: dept,
  }));

  return (
    <div className="regi-container">
      <div className="regi-header">
        <h1 className="regi-title">Department User Creation</h1>
        <p className="regi-subtitle">Limit User to One for each Department.</p>
      </div>

      <div className="regi-content">
        {successMessage && (
          <div className="success-message">
            <div className="success-content">
              <span className="success-icon">✓</span>
              <div className="success-text">{successMessage}</div>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="error-message">
            <div className="error-content">
              <span className="error-icon">!</span>
              <div className="error-text">{errorMessage}</div>
            </div>
          </div>
        )}

        {departmentsError && (
          <div className="error-message">
            <div className="error-content">
              <span className="error-icon">!</span>
              <div className="error-text">{departmentsError}</div>
            </div>
          </div>
        )}

        {/* Show OTP error in form step when it exists */}
        {otpError && otpStep === "form" && !errorMessage && (
          <div className="error-message">
            <div className="error-content">
              <span className="error-icon">!</span>
              <div className="error-text">{otpError}</div>
            </div>
          </div>
        )}

        {otpStep === "verify-otp" && (
          <div className="otp-verification-card">
            <div className="otp-header">
              <h3>Verify Phone Number</h3>
              <p>Enter the 6-digit OTP sent to your phone</p>
              {otpCountdown > 0 && (
                <div className="otp-timer">
                  OTP expires in: {formatCountdown(otpCountdown)}
                </div>
              )}
            </div>
            
            <div className="otp-body">
              <div className="form-group">
                <label htmlFor="otp" className="form-label">
                  OTP Verification Code *
                </label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setOtp(value);
                    if (otpError) setOtpError("");
                  }}
                  className={`form-input ${otpError ? "error" : ""}`}
                  placeholder="123456"
                  maxLength={6}
                  disabled={otpLoading}
                  autoFocus
                />
              </div>

              {otpError && (
                <div className="error-message" style={{ marginBottom: "15px" }}>
                  <div className="error-content">
                    <span className="error-icon">!</span>
                    <div className="error-text">{otpError}</div>
                  </div>
                </div>
              )}

              <div className="otp-actions">
                <button
                  type="button"
                  onClick={cancelOTPVerification}
                  className="reset-btn"
                  disabled={otpLoading}
                >
                  Cancel
                </button>

                {otpCountdown > 0 ? (
                  <button
                    type="button"
                    onClick={verifyOTPAndCreateUser}
                    className="submit-btn"
                    disabled={otpLoading}
                  >
                    {otpLoading ? (
                      <>
                        <span className="spinner"></span>
                        Verifying & Creating...
                      </>
                    ) : (
                      "Verify & Create User"
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={resendOTP}
                    className="submit-btn"
                    disabled={otpLoading}
                  >
                    {otpLoading ? (
                      <>
                        <span className="spinner"></span>
                        Resending OTP...
                      </>
                    ) : (
                      "Resend OTP"
                    )}
                  </button>
                )}
              </div>

              <div className="user-details-preview">
                <h4>User Details:</h4>
                <div className="preview-grid">
                  <div className="preview-item">
                    <span className="preview-label">Name:</span>
                    <span className="preview-value">{formData.cs_name}</span>
                  </div>
                  <div className="preview-item">
                    <span className="preview-label">Email:</span>
                    <span className="preview-value">
                      {formData.cs_user_email}
                    </span>
                  </div>
                  <div className="preview-item">
                    <span className="preview-label">Department:</span>
                    <span className="preview-value">
                      {formData.cs_user_department}
                    </span>
                  </div>
                  <div className="preview-item">
                    <span className="preview-label">Phone:</span>
                    <span className="preview-value">
                      {formData.cs_user_phone}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {otpStep === "form" && (
          <form onSubmit={handleSubmit} className="regi-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="cs_name" className="form-label">
                  Full Name
                </label>
                <input
                  type="text"
                  id="cs_name"
                  name="cs_name"
                  value={formData.cs_name}
                  onChange={handleChange}
                  className={`form-input ${errors.cs_name ? "error" : ""}`}
                  placeholder="Enter full name"
                  required
                  disabled={isSubmitting || otpLoading}
                />
                {errors.cs_name && (
                  <span className="error-text">{errors.cs_name}</span>
                )}
                <small className="input-hint">Primary name for the user</small>
              </div>

              <div className="form-group">
                <label htmlFor="cs_user_email" className="form-label">
                  Email Address
                </label>
                <input
                  type="email"
                  id="cs_user_email"
                  name="cs_user_email"
                  value={formData.cs_user_email}
                  onChange={handleChange}
                  className={`form-input ${errors.cs_user_email ? "error" : ""}`}
                  placeholder="user@example.com"
                  required
                  disabled={isSubmitting || otpLoading}
                />
                {errors.cs_user_email && (
                  <span className="error-text">{errors.cs_user_email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="cs_user_phone" className="form-label">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="cs_user_phone"
                  name="cs_user_phone"
                  value={formData.cs_user_phone}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      cs_user_phone: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  className={`form-input ${errors.cs_user_phone ? "error" : ""}`}
                  placeholder="9876543210"
                  maxLength={10}
                  inputMode="numeric"
                  required
                  disabled={isSubmitting || otpLoading}
                />

                {errors.cs_user_phone && (
                  <span className="error-text">{errors.cs_user_phone}</span>
                )}
                <small className="input-hint">
                  Will receive OTP via SMS for verification
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="cs_user_department" className="form-label">
                  Department
                </label>
                {departmentsLoading ? (
                  <div className="form-input loading">
                    Loading departments...
                  </div>
                ) : (
                  <Select
                    id="cs_user_department"
                    name="cs_user_department"
                    value={
                      departmentOptions.find(
                        (option) => option.value === formData.cs_user_department
                      ) || null
                    }
                    onChange={handleDepartmentChange}
                    options={departmentOptions}
                    placeholder="Select Department"
                    isLoading={departmentsLoading}
                    isDisabled={
                      departmentsLoading || isSubmitting || otpLoading
                    }
                    classNamePrefix="form-select"
                    menuPlacement="bottom"
                    menuPosition="fixed"
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        padding: "12px 16px",
                        border: state.isFocused
                          ? "2px solid #4dabf7"
                          : errors.cs_user_department
                          ? "2px solid #fa5252"
                          : "2px solid #e9ecef",
                        borderRadius: "8px",
                        fontSize: "15px",
                        background: "white",
                        boxShadow: state.isFocused
                          ? "0 0 0 3px rgba(77, 171, 247, 0.1)"
                          : "none",
                        "&:hover": { borderColor: "#4dabf7" },
                        opacity:
                          departmentsLoading || isSubmitting || otpLoading
                            ? 0.7
                            : 1,
                      }),
                      menu: (provided) => ({
                        ...provided,
                        marginTop: "4px",
                        zIndex: 9999,
                      }),
                    }}
                  />
                )}
                {errors.cs_user_department && (
                  <span className="error-text">
                    {errors.cs_user_department}
                  </span>
                )}
                <small className="input-hint">
                  Username will be set to this department name
                </small>
              </div>
            </div>

            <div className="form-note">
              <p>
                <strong>Important Notes:</strong>
              </p>
              <ul>
                <li>Phone number verification is required using OTP sent via SMS</li>
                {/* <li>User ID will be auto-generated by the system</li> */}
                {/* <li>Phone number must be unique and valid</li> */}
                <li>OTP expires in 5 minutes</li>
              </ul>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleReset}
                className="reset-btn"
                disabled={isSubmitting || otpLoading || departmentsLoading}
              >
                Clear Form
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting || departmentsLoading || otpLoading}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Sending OTP...
                  </>
                ) : (
                  "Send OTP & Create User"
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {showSuccessDialog && createdUser && (
        <div className="modal-overlay">
          <div className="modal-dialog success-dialog">
            <div className="dialog-header">
              <div className="success-icon-large">✓</div>
              <h3>User Created Successfully!</h3>
            </div>

            <div className="dialog-body">
              <div className="user-details">
                <div className="detail-item">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{createdUser.cs_name}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">
                    {createdUser.cs_user_email}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">
                    {createdUser.cs_user_phone || "Not provided"}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Department:</span>
                  <span className="detail-value">
                    {createdUser.cs_user_department}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Created by:</span>
                  <span className="detail-value">
                    {createdUser.created_by || "System"}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Created At:</span>
                  <span className="detail-value">
                    {new Date(createdUser.cs_created_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="dialog-footer">
              <button onClick={handleCloseSuccessDialog} className="submit-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Regi;